'use client';
import { IRepository, IRepositoryEntry } from '@/types';

import * as Styled from './styled';
import { Row } from '@/ui/primatives';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import CodeBlock from './CodeBlock';

import DropdownMenu from '@/ui/DropdownMenu';
import useClipboard from '@/hooks/useClipboard';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';

const CodeSection: React.FC<{
  repository: IRepository;
  title: string;
  language: string;
  code: string;
  docsLink?: string;
  docsTitle?: string;
}> = ({ repository, title, language, code, docsLink, docsTitle }) => {
  const [copiedText, copy] = useClipboard(code, {
    event: {
      name: `${title.toLowerCase()}_download_code_copied`,
      payload: {
        repository_id: repository.id,
        repository_namespace: repository.namespace,
        repository_name: repository.name,
      },
    },
  });

  return (
    <Styled.CodeSection $copied={!!copiedText}>
      <Row alignItems="center" gap={10} justifyContent="space-between">
        <b onClick={copy}>{title}</b>
        <Row className="copy-indicator" alignItems="center" gap={5}>
          <span>{!!copiedText && 'copied'}</span>
          <Icon.Copy style={{ color: copiedText ? 'success' : 'default' }} onClick={copy} />
        </Row>
      </Row>
      <CodeBlock language={language} code={code} />
      {docsLink && (
        <a className="hint link-underline" href={docsLink} target="_blank">
          {docsTitle || title} documentation
        </a>
      )}
    </Styled.CodeSection>
  );
};

const getQualifiedDomain = (url: string) => {
  const parsedUrl = new URL(url);
  return parsedUrl.protocol + '//' + parsedUrl.hostname;
};

const DownloadButton: React.FC<{ repository: IRepository; entry: IRepositoryEntry; fileUrl?: string }> = ({
  repository,
  entry,
  fileUrl,
}) => {
  const qualifiedDomain = getQualifiedDomain(fileUrl!);
  const pythonCode = `from oxen import RemoteRepo\nrepo = RemoteRepo("${repository.namespace}/${repository.name}")\nrepo.download("${entry.resource?.path}", revision="${entry.resource?.version}")`;
  const oxenCode = `oxen download ${repository.namespace}/${repository.name} ${entry.resource?.path} --revision ${entry.resource?.version}`;
  // TODO: add pandas support once we can properly identify tabular format (parquet, csv, jsonl, etc)

  const isCsv = entry.data_type === 'tabular' && entry.extension === 'csv';
  const isParquet = entry.data_type === 'tabular' && entry.extension === 'parquet';

  const pandasCommand = isCsv ? 'pd.read_csv' : 'pd.read_parquet';
  const pandasCode = `import pandas as pd\ndf = ${pandasCommand}("${fileUrl}")\nprint(df.head())`;

  const httpCode = `curl -L ${fileUrl} -o ${entry.resource?.path}`;
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button.Basic nowrap small style={{ paddingLeft: 10, paddingRight: 10 }}>
          <DotsHorizontalIcon />
        </Button.Basic>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content noPadding>
        <Styled.MenuContent>
          <CodeSection
            repository={repository}
            title="Oxen"
            docsTitle="Oxen CLI"
            language="bash"
            code={oxenCode}
            docsLink="https://docs.oxen.ai/getting-started/install"
          />
          <CodeSection
            repository={repository}
            title="Python"
            language="python"
            code={pythonCode}
            docsTitle="Oxen Python library"
            docsLink="https://docs.oxen.ai/getting-started/python"
          />
          {entry.data_type === 'tabular' && (isCsv || isParquet) && (
            <CodeSection
              repository={repository}
              title="Pandas"
              language="python"
              code={pandasCode}
              docsTitle="Pandas read"
              docsLink="https://pandas.pydata.org/pandas-docs/stable/reference/api/pandas.read_csv.html"
            />
          )}
          <CodeSection repository={repository} title="HTTP" language="bash" code={httpCode} />
        </Styled.MenuContent>
        <DropdownMenu.Arrow />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default DownloadButton;
